/**
 * Politique de confidentialité — publique, sans auth.
 *
 * IMPORTANT : le contenu doit refléter honnêtement ce que fait réellement
 * l'application. Vérifié contre le code :
 *   - JWT stocké dans localStorage (auth.py / api.js) ; pas de cookie de suivi ;
 *   - audio prononciation : envoyé à OpenAI Whisper pour transcription puis
 *     à un LLM pour analyse ; NON stocké au-delà du traitement de la requête ;
 *   - user_words / lesson_sessions / etc. : stockés en MongoDB, associés à
 *     user_id ; conservés tant que le compte existe ;
 *   - Emergent LLM key : accès aux modèles Claude/Gemini (fallback) ; les
 *     prompts et réponses transitent par ces fournisseurs conformément à
 *     leurs propres politiques ;
 *   - IPs contact form : conservées avec chaque message contact (max 5 000 c.) ;
 *   - Pas de tracker analytics tiers dans /public/index.html.
 */
import LegalShell from "@/components/LegalShell";

function Article({ id, num, title, children }) {
    return (
        <section id={id} className="scroll-mt-28 space-y-3">
            <h2 className="font-serif text-xl md:text-2xl text-ink">
                <span className="text-inkSoft font-normal">{num}.</span> {title}
            </h2>
            <div className="text-[15px] leading-relaxed space-y-3">{children}</div>
        </section>
    );
}

export default function Privacy() {
    return (
        <LegalShell
            testid="page-privacy"
            eyebrow="Politique de confidentialité"
            title="Politique de confidentialité."
            subtitle="Ce que nous collectons, pourquoi, avec qui nous le partageons, comment vous en garder le contrôle. Dernière mise à jour : février 2026."
        >
            <Article id="responsable" num="1" title="Responsable du traitement">
                <p>
                    Lexia School est éditée par Lexia School (« l&apos;Éditeur »). Pour toute question relative aux
                    données personnelles, vous pouvez écrire à{" "}
                    <a href="mailto:support@openmeta.fr" className="text-terracotta hover:underline">support@openmeta.fr</a>.
                </p>
            </Article>

            <Article id="donnees-collectees" num="2" title="Données que nous collectons">
                <p>À la création du compte et pendant l&apos;utilisation, nous conservons :</p>
                <ul className="list-disc pl-5 space-y-1.5">
                    <li>
                        <strong>Données de compte</strong> — email, prénom, mot de passe (haché avec bcrypt, jamais
                        stocké en clair), date de création.
                    </li>
                    <li>
                        <strong>Profil d&apos;apprentissage</strong> — langue cible, niveau CECRL, objectif, temps
                        quotidien, préférences de voix, opt-in au classement hebdomadaire.
                    </li>
                    <li>
                        <strong>Progression</strong> — leçons commencées et terminées, mots rencontrés / actifs /
                        maîtrisés / fragiles, historique de révisions, streak (série de jours actifs), scores de
                        compétences.
                    </li>
                    <li>
                        <strong>Interactions vocales</strong> — voir la section 3 dédiée aux données audio.
                    </li>
                    <li>
                        <strong>Contenus de conversation</strong> — messages échangés avec les conversations IA,
                        podcasts et quiz générés, corrections. Conservés pour vous permettre de revoir vos sessions.
                    </li>
                    <li>
                        <strong>Certificats & partages</strong> — snapshots figés lorsque vous générez un certificat ou
                        une carte de progression publique (identifiant UUID, prénom, langue, niveau).
                    </li>
                    <li>
                        <strong>Journaux techniques</strong> — adresse IP et user-agent stockés lorsque vous utilisez
                        le formulaire de contact ; sinon uniquement en logs applicatifs temporaires.
                    </li>
                </ul>
            </Article>

            <Article id="audio" num="3" title="Vos données audio (micro)">
                <p>
                    Lorsque vous utilisez les fonctionnalités vocales — exercice de prononciation, conversation temps
                    réel avec Lexia, question du jour vocale — votre voix est capturée par votre navigateur puis :
                </p>
                <ol className="list-decimal pl-5 space-y-1.5">
                    <li>
                        <strong>Transmise à OpenAI (Whisper)</strong> pour transcription en texte. L&apos;audio n&apos;est
                        pas persisté sur nos serveurs au-delà du temps de traitement de votre requête.
                    </li>
                    <li>
                        <strong>La transcription est ensuite envoyée à un modèle LLM</strong> (OpenAI ou Anthropic
                        selon disponibilité) pour analyse de prononciation et retour pédagogique.
                    </li>
                    <li>
                        <strong>Seuls le retour textuel et le score</strong> sont conservés en base — l&apos;audio
                        brut, non.
                    </li>
                </ol>
                <p>
                    Les sessions vocales temps réel (Lexia via OpenAI Realtime API) utilisent une connexion WebRTC
                    directe entre votre navigateur et OpenAI, via un jeton éphémère émis par notre backend. Nous ne
                    recevons pas votre voix brute ; nous ne conservons que les événements de haut niveau (démarrage,
                    fin, mots pratiqués transmis par l&apos;IA elle-même).
                </p>
                <p>
                    Vous pouvez refuser l&apos;accès au micro à tout moment — l&apos;Application reste utilisable, seules
                    les fonctionnalités vocales sont désactivées.
                </p>
            </Article>

            <Article id="finalites" num="4" title="Finalités du traitement">
                <p>Nous utilisons ces données uniquement pour :</p>
                <ul className="list-disc pl-5 space-y-1">
                    <li>fournir et personnaliser votre expérience d&apos;apprentissage ;</li>
                    <li>afficher votre progression et calibrer vos révisions ;</li>
                    <li>émettre les certificats et cartes de partage que <em>vous</em> demandez ;</li>
                    <li>répondre à vos messages via le formulaire de contact ;</li>
                    <li>maintenir et améliorer techniquement l&apos;Application.</li>
                </ul>
                <p>Nous <strong>ne vendons pas</strong> vos données. Nous <strong>ne les utilisons pas</strong> à des
                    fins publicitaires ou de profilage marketing.</p>
            </Article>

            <Article id="sous-traitants" num="5" title="Sous-traitants techniques">
                <p>Pour fournir le service, nous nous appuyons sur les prestataires suivants :</p>
                <ul className="list-disc pl-5 space-y-1.5">
                    <li>
                        <strong>OpenAI</strong> — génération de contenu, TTS (voix), Whisper (transcription), Realtime
                        API (conversation vocale). Politique de confidentialité :{" "}
                        <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-terracotta hover:underline">openai.com/policies/privacy-policy</a>.
                    </li>
                    <li>
                        <strong>Anthropic (Claude)</strong> — fallback pour la génération de contenu textuel.
                    </li>
                    <li>
                        <strong>Google (Gemini)</strong> — génération d&apos;illustrations mnémoniques.
                    </li>
                    <li>
                        <strong>MongoDB Atlas</strong> — base de données managée (hébergée en Europe).
                    </li>
                    <li>
                        <strong>Resend</strong> — envoi d&apos;emails transactionnels depuis le formulaire de contact
                        (uniquement si activé côté serveur).
                    </li>
                </ul>
                <p>
                    Ces prestataires traitent vos données conformément à leurs propres politiques de confidentialité.
                    Aucune donnée n&apos;est vendue ou cédée à des tiers non listés ci-dessus.
                </p>
            </Article>

            <Article id="stockage-local" num="6" title="Stockage local dans votre navigateur">
                <p>
                    L&apos;Application stocke dans le <code className="text-xs bg-paperAlt border border-lexiBorder rounded px-1 py-0.5">localStorage</code>{" "}
                    de votre navigateur :
                </p>
                <ul className="list-disc pl-5 space-y-1">
                    <li>un jeton d&apos;authentification (JWT) qui vous maintient connecté·e ;</li>
                    <li>des préférences d&apos;affichage (thème, dernière langue sélectionnée…).</li>
                </ul>
                <p>
                    Nous n&apos;utilisons <strong>aucun cookie de suivi tiers</strong> et <strong>aucun outil
                        d&apos;analytics comportemental</strong> (Google Analytics, Meta Pixel, etc.).
                </p>
            </Article>

            <Article id="duree" num="7" title="Durée de conservation">
                <ul className="list-disc pl-5 space-y-1.5">
                    <li>
                        <strong>Compte et progression</strong> — conservés tant que votre compte est actif. En cas de
                        suppression, tout est effacé dans un délai maximum de 30 jours.
                    </li>
                    <li>
                        <strong>Certificats et cartes de partage</strong> — conservés jusqu&apos;à révocation
                        explicite de votre part.
                    </li>
                    <li>
                        <strong>Messages de contact</strong> — conservés 3 ans à des fins de suivi client.
                    </li>
                    <li>
                        <strong>Audio brut</strong> — non conservé au-delà du traitement d&apos;une requête (secondes).
                    </li>
                </ul>
            </Article>

            <Article id="droits" num="8" title="Vos droits (RGPD)">
                <p>Conformément au Règlement général sur la protection des données (RGPD), vous disposez des droits :</p>
                <ul className="list-disc pl-5 space-y-1">
                    <li><strong>Accès</strong> — obtenir une copie de vos données ;</li>
                    <li><strong>Rectification</strong> — corriger une information inexacte ;</li>
                    <li><strong>Effacement</strong> — demander la suppression de votre compte et de vos données ;</li>
                    <li><strong>Portabilité</strong> — recevoir vos données dans un format lisible par machine ;</li>
                    <li><strong>Opposition</strong> — vous opposer à un traitement (ex. : opt-out du classement) ;</li>
                    <li><strong>Limitation</strong> — restreindre temporairement un traitement.</li>
                </ul>
                <p>
                    Pour exercer ces droits, écrivez à{" "}
                    <a href="mailto:support@openmeta.fr" className="text-terracotta hover:underline">support@openmeta.fr</a>{" "}
                    depuis l&apos;adresse email associée à votre compte. Nous vous répondons sous 30 jours maximum.
                </p>
                <p>
                    Vous avez également le droit d&apos;introduire une réclamation auprès de la CNIL (Commission
                    Nationale de l&apos;Informatique et des Libertés) — <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-terracotta hover:underline">cnil.fr</a>.
                </p>
            </Article>

            <Article id="modifs" num="9" title="Modifications">
                <p>
                    Cette politique peut être mise à jour. Les évolutions substantielles font l&apos;objet d&apos;une
                    notification lors de votre prochaine connexion.
                </p>
            </Article>
        </LegalShell>
    );
}
